import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { activityApi } from "../../services/api";

const ActivityDetail = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const response = await activityApi.getActivityById(activityId);
        setActivity(response);
      } catch (err) {
        setError(err.message || "Failed to load activity details");
      } finally {
        setLoading(false);
      }
    };
    if (activityId) fetchActivity();
  }, [activityId]);

  if (loading) return <div className="min-h-screen bg-background"><Header /><LoadingSpinner text="Loading activity..." /></div>;
  if (error) return <div className="min-h-screen bg-background"><Header /><ErrorMessage message={error} /></div>;
  if (!activity) return null;

  const data = activity.activityData || activity;
  const cat = data.category || activity.category || "Activity";
  const inclusions = activity.inclusions || [];
  const exclusions = activity.exclusions || [];
  const itinerary = activity.itineraryData || [];
  const faq = activity.faq || [];
  const photos = activity.photos || [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
          <div className="absolute inset-0">
            <img
              src={data.heroImage || "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&h=600&fit=crop"}
              alt={data.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/30 mb-4">
                {cat}
              </span>
              <h1 className="section-heading text-white mb-4">
                {data.name}
              </h1>
              <p className="text-lg text-white/80 mb-6">{data.subtitle}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                <span className="flex items-center gap-1.5">
                  <Icon name="Clock" size={15} />
                  {data.duration}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-headline text-2xl text-gray-900 mb-4" style={{ fontWeight: 300 }}>Overview</h2>
                <div className="prose prose-gray max-w-none">
                  {data.overview?.split("\n\n").map((p, i) => (
                    <p key={i} className="text-gray-600 leading-relaxed mb-4">{p}</p>
                  ))}
                </div>

                {data.highlights && data.highlights.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-headline text-xl text-gray-900 mb-4" style={{ fontWeight: 300 }}>Highlights</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {data.highlights.map((h, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100">
                          <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon name="Check" size={12} className="text-accent" />
                          </div>
                          <span className="text-sm text-gray-700">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-[120px] space-y-6">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <Button
                      onClick={() => navigate("/booking-consultation-center", { state: { activityId, activityName: data.name } })}
                      className="w-full"
                      size="lg"
                    >
                      <Icon name="Calendar" size={16} />
                      Book This Activity
                    </Button>
                    <button
                      onClick={() => navigate("/booking-consultation-center")}
                      className="w-full mt-2 text-sm text-gray-500 hover:text-accent py-2"
                    >
                      Ask a Question
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Itinerary */}
        {itinerary.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-headline text-2xl text-gray-900 mb-8 text-center" style={{ fontWeight: 300 }}>Itinerary</h2>
              <div className="max-w-3xl mx-auto">
                {itinerary.map((day, i) => (
                  <div key={i} className="relative pl-8 pb-8 last:pb-0">
                    <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-200 last:hidden" />
                    <div className="absolute left-[-5px] top-1 w-[11px] h-[11px] rounded-full bg-accent border-2 border-white" />
                    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-xs font-semibold text-accent uppercase">Day {day.day}</span>
                          <h3 className="font-headline text-gray-900" style={{ fontWeight: 300 }}>{day.title}</h3>
                        </div>
                        {day.duration && (
                          <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                            <Icon name="Clock" size={11} />
                            {day.duration}
                          </span>
                        )}
                      </div>
                      {day.location && <p className="text-xs text-gray-400 mb-2">{day.location}</p>}
                      <p className="text-sm text-gray-600 leading-relaxed">{day.description}</p>
                      {day.highlights && day.highlights.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {day.highlights.map((h, j) => (
                            <span key={j} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{h}</span>
                          ))}
                        </div>
                      )}
                      {day.meals && (
                        <p className="text-xs text-gray-400 mt-2">
                          <Icon name="Coffee" size={11} className="inline mr-1" />
                          {day.meals}
                        </p>
                      )}
                      {day.notes && (
                        <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mt-3">
                          <Icon name="Info" size={11} className="inline mr-1" />
                          {day.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Inclusions / Exclusions */}
        {(inclusions.length > 0 || exclusions.length > 0) && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {inclusions.length > 0 && (
                  <div className="bg-green-50 rounded-2xl p-6 border border-green-100">
                    <h3 className="font-headline text-gray-900 mb-4 flex items-center gap-2" style={{ fontWeight: 300 }}>
                      <Icon name="CheckCircle" size={18} className="text-green-600" />
                      What's Included
                    </h3>
                    {inclusions.map((section, i) => (
                      <div key={i} className="mb-4 last:mb-0">
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">{section.title}</h4>
                        <ul className="space-y-1.5">
                          {section.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                              <Icon name="Check" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                {exclusions.length > 0 && (
                  <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                    <h3 className="font-headline text-gray-900 mb-4 flex items-center gap-2" style={{ fontWeight: 300 }}>
                      <Icon name="XCircle" size={18} className="text-red-500" />
                      What's Excluded
                    </h3>
                    {exclusions.map((section, i) => (
                      <div key={i} className="mb-4 last:mb-0">
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">{section.title}</h4>
                        <ul className="space-y-1.5">
                          {section.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                              <Icon name="X" size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-headline text-2xl text-gray-900 mb-8 text-center" style={{ fontWeight: 300 }}>Frequently Asked Questions</h2>
              <div className="space-y-3">
                {faq.map((item, i) => (
                  <details key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="font-medium text-gray-900 text-sm pr-4">{item.question}</span>
                      <Icon name="ChevronDown" size={16} className="text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-headline text-2xl text-gray-900 mb-8 text-center" style={{ fontWeight: 300 }}>Gallery</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="aspect-[4/3] rounded-xl overflow-hidden">
                    <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-accent">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-headline text-3xl text-white mb-4" style={{ fontWeight: 300 }}>Ready for this Experience?</h2>
            <p className="text-white/80 mb-8">Contact us to book or customize this activity to your preferences.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/booking-consultation-center", { state: { activityId, activityName: data.name } })}
                variant="secondary"
                size="lg"
                className="bg-white text-accent hover:bg-white/90"
              >
                <Icon name="Calendar" size={16} />
                Book Now
              </Button>
              <Button
                onClick={() => navigate("/activities")}
                variant="outline"
                size="lg"
                className="border-white/40 text-white hover:bg-white/10"
              >
                <Icon name="ArrowLeft" size={16} />
                Browse All Activities
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <div className="col-span-2">
              <a href="/" className="flex items-center gap-3 mb-5 group w-fit">
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl overflow-hidden ring-2 ring-primary/30">
                    <img src="/assets/images/logo.jpeg" alt="Sherpa Trekker" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div>
                  <span className="font-headline font-bold text-lg text-white block">Sherpa Trekker</span>
                </div>
              </a>
              <p className="text-white/65 text-sm leading-relaxed max-w-xs mb-6">Guided by generations of Sherpa wisdom, we create transformative Himalayan experiences.</p>
              <div className="flex items-center gap-2 text-sm text-white/65 mb-2">
                <Icon name="Phone" size={14} className="text-primary" /> +977-9851157213
              </div>
              <div className="flex items-center gap-2 text-sm text-white/65">
                <Icon name="Mail" size={14} className="text-primary" /> imsherpatrekker@gmail.com
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Activities</h4>
              <ul className="space-y-2.5">
                <li><a href="/activities?category=Heli+Tour" className="text-sm text-white/60 hover:text-white">Heli Tours</a></li>
                <li><a href="/activities?category=Wildlife" className="text-sm text-white/60 hover:text-white">Wildlife</a></li>
                <li><a href="/activities?category=Cultural+Tour" className="text-sm text-white/60 hover:text-white">Cultural Tours</a></li>
                <li><a href="/activities?category=Festival+Tour" className="text-sm text-white/60 hover:text-white">Festivals</a></li>
                <li><a href="/activities?category=Wellness" className="text-sm text-white/60 hover:text-white">Wellness</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                <li><a href="/trek-discovery-hub" className="text-sm text-white/60 hover:text-white">Trek Discovery Hub</a></li>
                <li><a href="/sherpa-guide-profiles" className="text-sm text-white/60 hover:text-white">Our Guides</a></li>
                <li><a href="/traveler-stories-hub" className="text-sm text-white/60 hover:text-white">Traveler Stories</a></li>
                <li><a href="/booking-consultation-center" className="text-sm text-white/60 hover:text-white">Book Consultation</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-center text-sm text-white/50">
            &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ActivityDetail;
